import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useClickOutside, useTripDetails } from "../../services/hooks";
import { usePlantContext } from "../../context/plantContext";
import { Input } from "../utils/index";
import { useLocation, useNavigate } from "react-router-dom";


export default function VehicleSearchDropdown() {
	const [searchValue, setSearchValue] = useState("");
	const [showDropdown, setShowDropdown] = useState(false);
	const { plantOptions } = usePlantContext();
	const { tripDetail, isLoading, getTripDetail } = useTripDetails();
	const dropdownRef = useRef(null);
	const debounceTimer = useRef(null);
	const navigate = useNavigate();
	const location = useLocation();
	const uniqueTrips = useMemo(() => {
		const seen = new Set();
		return tripDetail?.filter((trip) => {
			if (seen.has(trip.vehicleNumber)) return false;
			seen.add(trip.vehicleNumber);
			return true;
		}) || [];
	}, [tripDetail]);

	useClickOutside(dropdownRef, () => {
		setShowDropdown(false);
	});
	const prevPathname = useRef(location.pathname);

	useEffect(() => {
		const wasOnManageTrip = prevPathname.current.includes("manage-trip");
		const isNowOnManageTrip = location.pathname.includes("manage-trip");

		// Only clear if user is LEAVING manage-trip (was there, now isn't)
		if (wasOnManageTrip && !isNowOnManageTrip) {
			setSearchValue("");
			setShowDropdown(false);
		}

		prevPathname.current = location.pathname; 
	}, [location.pathname]);
	const fetchTrips = useCallback(
		(value) => {
			if (!value || value.trim().length === 0) {
				setShowDropdown(false);
				return;
			}
			const allPlantIds = plantOptions?.map((opt) => opt.value) || [];
			const filter = {
				searchBy: ["vehicleNumber"],
				search: [value.trim()],
				start: new Date(Date.now() - 250 * 24 * 60 * 60 * 1000).toISOString(),
				end: new Date().toISOString(),
				plantId: allPlantIds,
			};
			getTripDetail(filter)
				.unwrap()
				.then((res) => {
					setShowDropdown(true);
				})
				.catch(() => {
					setShowDropdown(false);
				});
		},
		[getTripDetail, plantOptions]
	);

	const handleInputChange = (e) => {
		const value = e.target.value;
		setSearchValue(value);
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
		}
		debounceTimer.current = setTimeout(() => {
			fetchTrips(value);
		}, 500);
	};

	useEffect(() => {
		return () => {
			if (debounceTimer.current) clearTimeout(debounceTimer.current);
		};
	}, []);

	const handleItemClick = (trip) => {
		setShowDropdown(false);
		setSearchValue(trip.vehicleNumber || "");
		const targetPath = `/manage-trip?entryId=${trip._id}`;
		if (window.location.pathname + window.location.search === `/manage-trip?entryId=${trip._id}`) {
			navigate("/manage-trip", { replace: true });
			setTimeout(() => navigate(targetPath, { replace: true }), 0);
		} else {
			navigate(targetPath);
		}
	};

	const handleClear = () => {
		setSearchValue("");
		setShowDropdown(false);
	};

	return (
		<div className="vehicle-search-dropdown" ref={dropdownRef} style={{marginTop:"1px"}}>
			<Input
				label="Track by Vehicle Number"
				placeholder="Enter vehicle number"
				name="vehicleSearch"
				value={searchValue}
				onChange={handleInputChange}
				sx={{ minWidth: "220px" }}
				adornment={{
					is: searchValue ? true : false,
					onClick: handleClear,
					type: isLoading ? "loader" : "CloseIcon",
				}}
			/>
			{showDropdown && (
				<ul className="vehicle-search-results">
					{uniqueTrips?.length > 0 ? (
						uniqueTrips.map((trip) => (
							<li key={trip._id} className="vehicle-search-result-item" onClick={() => handleItemClick(trip)}>
								<span style={{ fontWeight: "bold" }}>Vehicle No. </span>
								<span>{trip.vehicleNumber}</span>
								<span style={{ fontWeight: "bold" }}>, Trip ID: </span>
								<span>{trip.trip_counter}</span>
							</li>
						))
					) : (
						<li className="vehicle-search-result-item" style={{ textAlign: "center" }}>
							{isLoading ? "Searching..." : "No data found"}
						</li>
					)}
				</ul>
			)}
		</div>
	);
}
